/**
 * @lume/shared — AUTO-RESTRIÇÃO TIPADA (Motor Neutro B2c; o 3º modo de autoridade da Fase 2).
 *
 * Fecha o `selfRestrictedScopes` que a B2 deixou com feed diferido. É a peça SEPARADA da
 * Decisão 1 regra (c) do DRIVER: um «não» de outra natureza — não é papel, não é escopo de
 * terceiro, não é assurance —, logo **não** é `mode` de autoridade nem `disposition` do porteiro.
 *
 * ★ O QUE A AUDITORIA NA FONTE ACHOU (spec §0), e que corrige a B2: a premissa «auto-restrição
 * NÃO tem substrato» é **FALSA como escrita**. O motor já tem DUAS auto-restrições VIVAS e
 * ENFORÇADAS — e é justamente por serem ad-hoc que faltava o TIPO:
 *
 *   1. `Person.proactiveOptOutAt` (STOP) — enforçada em `proactive-dispatch-guard.service.ts:82-90`
 *      («a marca da Person é SOBERANA»). **Perpétua e sem escopo.** A própria B3 a classificou
 *      em `NOT_A_DEADLINE_COLUMNS` (`typed-deadline.ts:471`): «bloqueio perpétuo, sem termo …
 *      é flag, não prazo».
 *   2. `LgpdRequest(requestType='opposition', status='completed')` — o MESMO check duplicado em
 *      **7 dispatchers** (`outbound-task-dispatch:133` · `appointment-soa-dispatch:102` ·
 *      `appointment-confirmation-dispatch:121` · `appointment-journey-dispatch` ·
 *      `manual-soa-dispatch` · `data-change-confirmation:127` · `verification-dispatch`).
 *      **Tudo-ou-nada.**
 *
 * ★ E a IRREVOGABILIDADE de hoje é ACIDENTAL: o comentário do schema (`schema.prisma:1038`)
 * afirma «Reversível (set null = re-inscreve)», mas **nenhum writer em `src` zera a coluna** —
 * só o `updateMany` que a SETA (`proactive-opt-out.service.ts:60-63`). Hoje o STOP é irrevogável
 * por AUSÊNCIA de caminho, não por declaração: uma propriedade que ninguém decidiu.
 *
 * O que esta primitiva acrescenta às duas: **ESCOPO** (o que está restrito), **TERMO** (até
 * quando) e **REVOGABILIDADE DECLARADA** (quem pode desfazer, e quando ninguém pode).
 *
 * ── O PISO (inafrouxável; só ESTREITA — nenhuma regra pode conceder nem cobrir menos) ────────
 *   R1 — **APPEND-ONLY.** Escopo e termo são IMUTÁVEIS: nunca se estreita o escopo nem se
 *        encurta o prazo. Não existe rota `PATCH`/`PUT` (garantido na API); o único write
 *        pós-criação é `revokedAt`, guardado por R2. Fecha a alavanca óbvia de burla —
 *        editar em vez de revogar.
 *   R2 — **irrevogável antes do termo é impossível para QUALQUER ator**, inclusive
 *        plataforma/operador/admin (`canRevokeSelfRestriction`). Sem exceção em código: uma
 *        exceção esvaziaria a única garantia que a fatia entrega. A via legal/regulatória
 *        (molde Override Type-6, DocumentationGated) é **fatia própria** — residual R-C.
 *   R3 — **fail-CLOSED em entrada corrompida**, sempre na direção de CONTINUAR restringindo:
 *        `scopes` não-array/itens não-string ⇒ **blanket**; `revocability` desconhecida ⇒
 *        **irrevogável**; `until` não-parseável ⇒ **perpétua**; `now` não-parseável ⇒ **não
 *        libera**. Lookup por `Set` (não sofre herança de protótipo — classe C3/M2 da B2), e
 *        os `Set` são construídos NESTE arquivo, sem o trap de barrel circular da A3.
 *
 * O que é LIVRE (config/caller): os **códigos de escopo** cunhados além dos reservados, o
 * **termo**, a **base** (proveniência) e a escolha entre as 2 revogabilidades.
 *
 * ── COMPOSIÇÃO COM A B3 (declarada, não escondida) ───────────────────────────────────────────
 * A disposição de cada restrição vem de `resolveDeadlineDisposition` com polaridade
 * **`blocks-until-elapsed`** — a INVERTIDA, a única em que um `now >= deadline` ingênuo agiria
 * do lado errado (liberaria a restrição exatamente enquanto ela deve valer). Isto torna a B2c
 * **a 1ª consumidora VIVA da primitiva da B3**, o que ESTREITA a claim de dormência dela (dito
 * também lá). O seed, o piso e o guard de drift da B3 seguem INTOCADOS, e a B2c cunha um `kind`
 * **NOVO** (`SELF_RESTRICTION_DEADLINE_KIND`), respeitando a R3 daquela fatia.
 *
 * ── ALCANCE HONESTO (residuais da spec §6) ───────────────────────────────────────────────────
 *   R-A três mecanismos coexistem (STOP · oposição LGPD · este) — a fatia TIPIFICA e INVENTARIA
 *        (`PRE_TYPED_SELF_RESTRICTIONS`), não unifica ⇒ quem audita «está restrito?» olha 3 lugares.
 *   R-B o enforcement alcança a comunicação INICIADA PELA PLATAFORMA. Uma restrição declarada
 *        NÃO impede o sujeito de agir na conversa nem o operador de lhe escrever.
 *   R-G e alcança **5** dos 7 caminhos: `manual-soa-dispatch`, `verification-dispatch` e
 *        `data-change-confirmation` checam oposição mas NÃO passam pelo guard.
 *   R-C irrevogabilidade não tem porta de saída (deliberado — vide R2).
 *   R-D fail-closed em corrupção DEGRADA comunicação; é a direção segura no eixo do
 *        consentimento. **Proibido** fechar isso com bypass de plataforma no leitor (lição R12).
 *
 * ── PRECONDIÇÕES NOMEADAS DA PROMOÇÃO (restringir AÇÃO, no porteiro) ─────────────────────────
 * Carrega A1–A6 (`access-decision.ts`) + as 4 da B2 (`decision-authority.ts`) + as próprias:
 *   (i)   resolver o **propósito/escopo da AÇÃO** no runtime do gate (aqui o propósito vem do
 *         caller; o gate não tem esse eixo hoje);
 *   (ii)  `selfRestrictedScopes ∩ requiredScope` compõe como **peça separada** de `deny` no
 *         orquestrador — nunca como `disposition` novo (Decisão 1 c);
 *   (iii) a **porta legal** (R-C) precisa existir ANTES de a restrição alcançar ação, senão um
 *         registro errado tranca o titular fora do próprio vínculo.
 *
 * Puro/total **sobre `SelfRestrictionEdge`s bem-formadas**: sem I/O, sem `Date.now`, sem lançar.
 * Claim escopada de propósito (NIT-1 da revisão): um `null`/`undefined` DENTRO do array de edges
 * lança — inalcançável via Prisma, e a direção do throw no guard é fail-closed (o dispatch falha,
 * a mensagem não sai). NEUTRO: `scopes` é string OPACA — quem nomeia
 * o que está restrito é o mecanismo/tenant, não o motor.
 *
 * Spec: docs/superpowers/specs/2026-07-25-motor-neutro-b2c-auto-restricao-tipada-design.md
 */
import { resolveDeadlineDisposition } from './typed-deadline';
// ───────────────────────────────────────────────────────────────────────────────
// Vocabulário fechado
// ───────────────────────────────────────────────────────────────────────────────
/**
 * Como a restrição se desfaz. `irrevocable-until-elapsed` é a que a Fase 2 pediu e que nenhuma
 * das 2 ad-hoc sabe declarar: **nem o próprio sujeito volta atrás** antes do termo.
 */
export const SELF_RESTRICTION_REVOCABILITIES = ['revocable', 'irrevocable-until-elapsed'];
/**
 * O `kind` de prazo desta primitiva — **NOVO**, cunhado aqui. Não está (e não pode estar) no
 * `PLATFORM_DEADLINE_SEED` da B3: redeclarar um kind reservado violaria a R3 daquela fatia.
 */
export const SELF_RESTRICTION_DEADLINE_KIND = 'self-restriction-window';
/**
 * Os propósitos de disparo proativo da PLATAFORMA, ancorados um a um nos 5 callers do
 * `ProactiveDispatchGuardService`. **Reservados:** a config pode cunhar códigos NOVOS, nunca
 * reinterpretar um destes (a lição do Blocker da B4, do lado da abertura).
 */
export const PLATFORM_RESTRICTION_SCOPES = [
    'appointment-reminder',
    'appointment-confirmation',
    'appointment-journey',
    'waitlist-offer',
    'outbound-task',
    // Fatia R-G (26/Jul) — os 2 caminhos que escapavam do chokepoint e agora o respeitam.
    'post-resolution-verification', // ADR-149, `verification-dispatch` (fonte `playbook_verification`)
    'manual-followup', // ADR-150, `manual-soa-dispatch` (fonte `manual`)
    // Fatia R-G9 (26/Jul) — o caminho MANUAL do operador (Brief 2B T8, `POST
    // /v1/proactive-conversations`). NÃO é «o operador escrevendo» (R-B da B2c): cria uma conversa
    // nova e dispara um template HSM, indistinguível do disparo agendado do lado de quem recebe.
    'operator-initiated-contact',
    // Espera Viva PR-3 (03/Ago) — retorno agendado que o PRÓPRIO cliente pediu no marco de
    // opções da fila («registrar e me retornem»). Restringível mesmo assim: uma auto-restrição
    // declarada DEPOIS do clique vence o clique (fonte `queue_return`, `queue-milestones.service`).
    'queue-return',
    // Relógio justo (04/Ago) — o wait-nudge (Item 379) era o ÚNICO dos 8 arcos proativos
    // fora deste chokepoint. Tolerável enquanto ele só falava com quem estava de fato na
    // fila; o rebaixamento indevido por idle-timeout derrubou essa premissa. Propósito
    // PRÓPRIO em vez de reusar `queue-return`: aquele é retorno que o cliente PEDIU, este é
    // aviso espontâneo de espera — reinterpretar um código reservado é o que a nota acima
    // proíbe (fonte `proactive-wait-nudge.service`).
    'queue-wait-notice',
    // Reaquecimento Julgado (04/Ago) — o 9º arco proativo, e o 1º a falar com quem parou de
    // responder no meio da CONDUÇÃO (os outros falam com quem está na fila, tem agenda marcada ou
    // recebeu iniciativa do operador). Propósito próprio: quem aceita lembrete de compromisso pode
    // legitimamente não querer ser reaquecido, e vice-versa.
    'reengagement',
];
/**
 * O propósito do T8, nomeado para que a PORTA (`initiate`) e o AVISO (`recipient-status`) não
 * possam divergir por typo. Se divergissem, o aviso mediria uma pergunta diferente da que a porta
 * responde — pior que não avisar, porque ensina o operador a não confiar no aviso.
 */
export const OPERATOR_INITIATED_CONTACT_SCOPE = 'operator-initiated-contact';
/**
 * ── R4 — os propósitos que NENHUMA auto-restrição cobre, nem blanket (fatia R-G, 26/Jul) ──────
 *
 * Não é uma exceção de conveniência: é a fronteira entre **comunicação que a pessoa pode
 * recusar** e **dever de cuidado do controlador**. A base legal é própria — LGPD art. 6º VII
 * (segurança) —, independente da preferência do titular, do mesmo modo que um banco não deixa
 * ninguém desativar o alerta de fraude.
 *
 * ★ O que FUNDAMENTA o membro de hoje (verificado na fonte, §0/PJ-3 da spec — e é o oposto do
 * que a intuição sugere): silenciar a confirmação cadastral **NÃO bloqueia a alteração**. O
 * `abortFlow` devolve o rascunho a `in_progress`, o `finalize` exige apenas `in_progress`, e o
 * disparo é opt-in. Logo calar a pergunta «foi você quem pediu?» deixa a mudança seguir com o
 * titular **nunca consultado** ⇒ silenciar EXPÕE em vez de proteger.
 *
 * ★ POR QUE a regra mora dentro de `isScopeSelfRestricted`, e não nos call-sites: um caller
 * futuro herda a garantia **sem saber que ela existe**. Pôr a checagem em cada chamador
 * repetiria, invertida, a lição da B2c — lá, uma variante nova de enum faria um caller alheio
 * **enviar**; aqui, um call-site que esquecesse a exceção faria um aviso de segurança **calar**.
 * São a mesma falha: a garantia tem de estar no lugar por onde todos passam.
 *
 * **RESERVADO** (a R5 da B4, do lado da abertura): o tenant cunha códigos novos, nunca
 * reinterpreta um destes. Os dois vocabulários são disjuntos, asserido por teste; e a porta
 * (`POST /v1/self-restrictions`) **recusa com 400** quem tentar declarar um deles, em vez de
 * aceitar e neutralizar em silêncio — uma garantia que não se pode dar não se aceita na porta.
 */
export const NON_RESTRICTABLE_PLATFORM_SCOPES = ['cadastral-change-confirmation'];
const PLATFORM_SCOPE_SET = new Set(PLATFORM_RESTRICTION_SCOPES);
const NON_RESTRICTABLE_SET = new Set(NON_RESTRICTABLE_PLATFORM_SCOPES);
const REVOCABILITY_SET = new Set(SELF_RESTRICTION_REVOCABILITIES);
export function isPlatformRestrictionScope(code) {
    return PLATFORM_SCOPE_SET.has(code);
}
/** R4 — o propósito é um dever de cuidado da plataforma, fora do alcance de qualquer restrição. */
export function isNonRestrictableScope(code) {
    return NON_RESTRICTABLE_SET.has(code);
}
/**
 * Inventário READ-ONLY das auto-restrições que já viviam ad-hoc — documentação-como-código
 * (molde do seed da B3: uma tabela em markdown apodrece, um `Record` tipado não). A fatia **não
 * muda o enforcement delas**; a coexistência é a dívida declarada (residual R-A).
 *
 * ★ RECONCILIADO na validação viva de 26/Jul (3º bloco do passivo). Este inventário é servido
 * por uma API LIVE (`GET /v1/persons/:id/self-restrictions`, cap `self-restriction-read`) e
 * afirmava, à data de escrita da B2c, que a oposição LGPD vivia em «7 dispatchers com o MESMO
 * check duplicado» e que lhe faltava «casa única». A fatia da **oposição LGPD centralizada**
 * (26/Jul, LIVE) desfez exatamente isso — 1 implementação / 8 chamadas, 7 cópias removidas — e
 * ninguém reconciliou o texto. Era a classe §10 do DRIVER (prosa divergindo da realidade
 * verificada) no artefato cuja única função é ser confiável, agravada por descrever como
 * FALTANTE algo que já havia sido entregue.
 *
 * Ancoragem por **texto** (nome do símbolo), nunca por número de linha: a citação
 * `proactive-dispatch-guard.service.ts:82-90` do STOP também já estava podre — o check migrou
 * para dentro de `evaluateRecipientRestriction` quando a R-G extraiu o predicado estreito. Foi a
 * lição da B3, aqui em segunda ocorrência.
 */
export const PRE_TYPED_SELF_RESTRICTIONS = {
    'proactive-communication-optout': {
        what: 'o titular responde STOP e o disparo proativo para (marca soberana em Person)',
        enforcedAt: 'ProactiveDispatchGuardService.evaluateRecipientRestriction (predicado estreito; decide ANTES da auto-restrição)',
        missing: 'escopo (é atômica) · termo (perpétua) · revogabilidade DECLARADA — hoje irrevogável por AUSÊNCIA de writer que zere a coluna, não por decisão',
    },
    'lgpd-opposition': {
        what: 'oposição do titular, concluída, bloqueia disparo proativo (art. 18 §2 — o sinal legalmente mais forte dos três)',
        enforcedAt: 'ProactiveDispatchGuardService.evaluateLgpdOpposition — leitura ÚNICA, consumida pelos 7 dispatchers na posição de cada um + pelo predicado estreito (T8 e oferta de fila)',
        missing: 'escopo (tudo-ou-nada: quem se opôs a UM tratamento para de receber TUDO) · termo · vínculo à Person (oposição com `requesterPersonId` nulo não é enforçada por ninguém — residual R-O5)',
    },
};
/** Normaliza um registro cru pelo piso. Só ESTREITA: nunca amplia escopo nem encurta termo. */
export function applySelfRestrictionFloor(edge) {
    // NOTA (NIT-3 da revisão): `revokedAt` fica FORA do piso — qualquer não-null conta como
    // revogada, inclusive um timestamp FUTURO corrompido no DB, que desligaria o enforcement.
    // Inalcançável pela API (o revoke grava `now()`); se um dia houver escrita externa, a
    // normalização de `revokedAt` entra aqui.
    // TRIM antes de filtrar (achado IMPORTANT-3 da revisão adversarial, confirmado por execução):
    // um escopo declarado como `' appointment-reminder '` NÃO casava o propósito
    // `'appointment-reminder'` ⇒ a pessoa acreditava ter restringido e os disparos continuavam
    // saindo. Normalizar aqui só ESTREITA (passa a bloquear onde não bloqueava). NÃO fazemos
    // lowercase: o código é OPACO e caixa pode ser significativa em código cunhado pelo tenant —
    // o confusável de caixa é barrado NA PORTA (400), não reinterpretado aqui.
    const scopes = Array.isArray(edge.scopes)
        ? edge.scopes
            .filter((s) => typeof s === 'string')
            .map((s) => s.trim())
            .filter((s) => s.length > 0)
        : [];
    const revocability = REVOCABILITY_SET.has(edge.revocability)
        ? edge.revocability
        : 'irrevocable-until-elapsed';
    const until = typeof edge.until === 'string' && Number.isFinite(Date.parse(edge.until)) ? edge.until : null;
    return { ...edge, scopes, revocability, until };
}
/** `true` só quando o termo existe E já decorreu. `now` corrompido ⇒ `false` (não libera). */
function hasElapsed(until, nowMs) {
    if (until === null || !Number.isFinite(nowMs))
        return false;
    const untilMs = Date.parse(until);
    return Number.isFinite(untilMs) && nowMs >= untilMs;
}
// ───────────────────────────────────────────────────────────────────────────────
// O observável
// ───────────────────────────────────────────────────────────────────────────────
export function resolveSelfRestrictions(params) {
    const nowMs = Date.parse(params.now);
    const entries = [];
    const blocked = new Set();
    let blanket = false;
    for (const raw of params.edges) {
        if (raw.revokedAt !== null)
            continue;
        const e = applySelfRestrictionFloor(raw);
        // A polaridade INVERTIDA da B3: `blocked` ANTES de decorrer, `released` DEPOIS.
        const disposition = resolveDeadlineDisposition({
            polarity: 'blocks-until-elapsed',
            elapsed: hasElapsed(e.until, nowMs),
        });
        entries.push({
            restrictionId: e.restrictionId,
            disposition,
            scopes: e.scopes,
            revocability: e.revocability,
            until: e.until,
        });
        if (disposition !== 'blocked')
            continue;
        if (e.scopes.length === 0)
            blanket = true;
        // R4 — um código não-restringível nomeado num registro (legado, ou gravado antes de a porta
        // existir) não entra na lista: o preview não pode dizer que aquilo está restrito quando o
        // predicado vai deixá-lo passar. A camada load-bearing é o curto-circuito de
        // `isScopeSelfRestricted`; esta aqui é honestidade do observável.
        else
            for (const s of e.scopes)
                if (!isNonRestrictableScope(s))
                    blocked.add(s);
    }
    return { entries, blockedScopes: [...blocked].sort(), blanketBlocked: blanket };
}
/**
 * O propósito consultado está restrito?
 *
 * `scope === null` = o caller NÃO declarou o seu propósito ⇒ **fail-CLOSED** quando há qualquer
 * restrição ativa. Sem restrição ativa, `null` não bloqueia nada (inércia estrita).
 *
 * R4 tem precedência sobre TUDO — inclusive sobre o blanket. O `null` segue fail-closed: não
 * declarar propósito nunca vira passe livre (quem quiser a isenção da R4 tem de nomeá-la).
 */
export function isScopeSelfRestricted(resolved, scope) {
    if (scope !== null && isNonRestrictableScope(scope))
        return false; // R4
    if (resolved.blanketBlocked)
        return true;
    if (scope === null)
        return resolved.blockedScopes.length > 0;
    return resolved.blockedScopes.includes(scope);
}
/**
 * R2 — o piso da revogação. Irrevogável + ainda bloqueando ⇒ **ninguém** revoga (nem a
 * plataforma). O piso normaliza ANTES de decidir, para que um registro com `revocability`
 * corrompida não caia em «revogável» silencioso.
 */
export function canRevokeSelfRestriction(edge, now) {
    if (edge.revokedAt !== null)
        return { allowed: false, reason: 'already-revoked' };
    const e = applySelfRestrictionFloor(edge);
    if (e.revocability === 'revocable')
        return { allowed: true, reason: null };
    return hasElapsed(e.until, Date.parse(now))
        ? { allowed: true, reason: null }
        : { allowed: false, reason: 'irrevocable-while-blocked' };
}
//# sourceMappingURL=self-restriction.js.map